import { useMutation } from '@tanstack/react-query'
import { BellRing, ChartColumn, Check, FileText, type LucideIcon } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, FieldLabel } from '@/components/ui/field'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phoneInput'
import { usePhoneField } from '../../hooks/usePhoneField'

export interface ExpertContact {
  fullName: string
  email?: string
  phone?: string
  /** Honeypot — humans leave this blank; a filled value marks the submission as a bot. */
  companyWebsite?: string
}

/**
 * Shared "Talk to an expert" dialog for the public funnel. Both the DOT flow and
 * the no-DOT new-authority flow use it; each passes its own `submit` that turns
 * the captured contact into a lead. On success it shows the broker-benefits
 * confirmation with a Start over link.
 */
export function TalkToExpertDialog({
  open,
  onOpenChange,
  submit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  submit: (contact: ExpertContact) => Promise<void>
}) {
  const navigate = useNavigate()
  const [fullName, setFullName] = useState('')
  const [mode, setMode] = useState<'mobile' | 'email'>('mobile')
  const [contact, setContact] = useState('')
  // Honeypot: stays empty for real users; bots that auto-fill every field
  // populate it and get dropped server-side.
  const [companyWebsite, setCompanyWebsite] = useState('')
  const phoneField = usePhoneField({ required: true })

  const mutation = useMutation({
    mutationFn: () =>
      submit({ fullName, companyWebsite, ...(mode === 'email' ? { email: contact } : { phone: contact }) }),
  })

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!contact.trim() || mutation.isPending) return
    if (mode === 'mobile' && !phoneField.check(contact)) return
    mutation.mutate()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {mutation.isSuccess ? (
          <div className="flex flex-col gap-6 py-2">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="bg-accent-green/15 flex size-16 items-center justify-center rounded-full">
                <div className="bg-accent-green flex size-11 items-center justify-center rounded-full">
                  <Check className="size-6 text-white" strokeWidth={3} />
                </div>
              </div>
              <DialogTitle className="text-2xl font-bold">You&apos;re all set!</DialogTitle>
              <p className="text-muted-foreground">A licensed agent will call you within 1 business day.</p>
            </div>

            <div className="flex flex-col gap-3">
              <div className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                What Cai does for you as your broker
              </div>
              <BrokerBenefit
                icon={ChartColumn}
                title="Monthly fleet health report"
                body="We monitor your FMCSA safety data and send you a report every month — so you always know where you stand before your carrier does."
              />
              <BrokerBenefit
                icon={FileText}
                title="Instant COI by text or email"
                body="Need a certificate of insurance? Request it anytime and get it in seconds — no phone call, no waiting on a broker."
              />
              <BrokerBenefit
                icon={BellRing}
                title="Renewal alert 90 days out"
                body="We'll benchmark your current premium against the market and flag any better options before your policy renews."
              />
            </div>

            <div className="flex justify-center">
              <Button variant="link" onClick={() => navigate('/insurance/start')}>
                Start over
              </Button>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Talk to an expert</DialogTitle>
              <DialogDescription>Leave your details and a licensed agent will reach out.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              {/* Honeypot: off-screen and hidden from assistive tech, so only bots fill it. */}
              <div
                aria-hidden
                className="pointer-events-none absolute -left-[9999px] h-0 w-0 overflow-hidden"
              >
                <Input
                  type="text"
                  name="company_website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={companyWebsite}
                  onChange={(e) => setCompanyWebsite(e.target.value)}
                />
              </div>
              <Field>
                <FieldLabel htmlFor="expert-full-name">Full name</FieldLabel>
                <Input
                  id="expert-full-name"
                  autoComplete="name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={mutation.isPending}
                />
              </Field>
              {mode === 'mobile' ? (
                <Field>
                  <FieldLabel htmlFor="expert-contact">Phone number</FieldLabel>
                  <PhoneInput
                    id="expert-contact"
                    autoComplete="tel"
                    value={contact}
                    onChange={(v) => setContact(v || '')}
                    disabled={mutation.isPending}
                    error={!!phoneField.error}
                  />
                  <FieldError message={phoneField.error} />
                </Field>
              ) : (
                <Field data-invalid={mutation.isError || undefined}>
                  <FieldLabel htmlFor="expert-contact">Email address</FieldLabel>
                  <Input
                    id="expert-contact"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={contact}
                    onChange={(e) => setContact(e.target.value)}
                    disabled={mutation.isPending}
                    aria-invalid={mutation.isError ? true : undefined}
                  />
                </Field>
              )}
              <Button
                type="button"
                variant="link"
                onClick={() => {
                  setMode(mode === 'mobile' ? 'email' : 'mobile')
                  setContact('')
                  phoneField.clear()
                }}
                className="text-cai-blue h-auto self-start p-0 text-sm font-medium"
              >
                Use {mode === 'mobile' ? 'email' : 'mobile'} instead
              </Button>
              {mutation.isError && (
                <p className="text-destructive text-sm">Something went wrong. Please try again.</p>
              )}
              <Button
                type="submit"
                disabled={!contact.trim() || mutation.isPending}
                className="mt-1"
                loading={mutation.isPending ? 'Sending…' : undefined}
              >
                Request a call
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function BrokerBenefit({ icon: Icon, title, body }: { icon: LucideIcon; title: string; body: string }) {
  return (
    <div className="bg-muted/40 flex gap-3 rounded-lg border p-4">
      <Icon className="text-cai-blue mt-0.5 size-5 shrink-0" aria-hidden />
      <div className="flex flex-col gap-1">
        <span className="text-foreground font-semibold">{title}</span>
        <p className="text-muted-foreground text-sm">{body}</p>
      </div>
    </div>
  )
}
